import React from "react";
import styled, { ThemeProvider } from "styled-components";
import PropTypes from "prop-types";
import { space, layout, variant } from "styled-system";
import Avatar from "../Avatar";
import Icon from "../Icon";
import Flex from "../Flex";
import Loading from "../Loading";
import { themeGet } from "@styled-system/theme-get";

const TagWrapper = styled.div`
  ${space}
  ${layout}
  display: flex;
  align-items: stretch;
  height: auto;
  max-height: max-content;
  min-height: ${(props) =>
    props.small
      ? themeGet("tagScale.tagHeightSmall")
      : themeGet("tagScale.tagHeightDefault")};
  justify-content: center;
  > button {
    margin: 0;
  }
`;

const TagValue = styled.button.attrs((props) => ({
  "aria-pressed":
    props.variant === "selected" || props.variant === "selectedHighlighted",
  type: "button",
}))`
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  box-shadow: none;
  font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
  line-height: 1;
  display: flex;
  gap: ${(props) => (props.small ? "3px" : "4px")};
  align-items: center;
  flex-wrap: wrap;
  user-select: text;
  font-size: ${(props) =>
    props.small
      ? themeGet("fontSizes.0")(props)
      : themeGet("fontSizes.1")(props)};
  font-weight: ${(props) => themeGet("fontWeights.2")(props)};
  white-space: nowrap;
  position: relative;
  z-index: 1;
  transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  cursor: pointer;
  border-radius: ${themeGet("tagScale.tagBorderRadius")};
  border-top-right-radius: ${(props) =>
    props.showEdit || props.showInfo || props.showRemove
      ? "0"
      : themeGet("tagScale.tagBorderRadius")};
  border-bottom-right-radius: ${(props) =>
    props.showEdit || props.showInfo || props.showRemove
      ? "0"
      : themeGet("tagScale.tagBorderRadius")};
  padding: ${(props) =>
    props.small
      ? themeGet("tagScale.tagPaddingSmall")
      : themeGet("tagScale.tagPaddingDefault")};
  border: 1px solid;

  ${(props) =>
    variant({
      variants: {
        default: {
          background: themeGet("colors.white")(props),
          color: themeGet("colors.primary")(props),
          borderColor: themeGet("colors.primary")(props),
          "&:hover, &:focus": {
            background: themeGet("colors.primaryLightest")(props),
            color: themeGet("colors.primaryDark")(props),
            borderColor: themeGet("colors.primaryDark")(props),
            div: {
              color: themeGet("colors.primaryDark")(props),
            },
            "div[class*='TagType']": {
              backgroundColor: themeGet("colors.primary20")(props),
            },
          },
        },
        selected: {
          background: themeGet("colors.primary")(props),
          color: themeGet("colors.white")(props),
          borderColor: themeGet("colors.primary")(props),
          "&:hover, &:focus": {
            background: themeGet("colors.primaryDark")(props),
            color: themeGet("colors.white")(props),
            borderColor: themeGet("colors.primaryDark")(props),
            div: {
              color: themeGet("colors.white")(props),
            },
            "div[class*='TagType']": {
              backgroundColor: "rgba(0,0,0,0.30)",
            },
          },
        },
        highlighted: {
          background: themeGet("colors.white")(props),
          color: themeGet("colors.warningDarker")(props),
          borderColor: themeGet("colors.warningDark")(props),
          "&:hover, &:focus": {
            background: themeGet("colors.warningLightest")(props),
            color: themeGet("colors.warningDarkest")(props),
            borderColor: themeGet("colors.warningDark")(props),
            div: {
              color: themeGet("colors.warningDarkest")(props),
            },
            "div[class*='TagType']": {
              backgroundColor: themeGet("colors.warning30")(props),
            },
          },
        },
        selectedHighlighted: {
          background: themeGet("colors.warningLighter")(props),
          color: themeGet("colors.black80")(props),
          borderColor: themeGet("colors.warningLighter")(props),
          "&:hover, &:focus": {
            background: themeGet("colors.warningLight")(props),
            color: themeGet("colors.black70")(props),
            borderColor: themeGet("colors.warningLight")(props),
            div: {
              color: themeGet("colors.black70")(props),
            },
            "div[class*='TagType']": {
              backgroundColor: "rgba(255,255,255,0.3)",
            },
          },
        },
        disabled: {
          background: themeGet("colors.hoverSurface")(props),
          color: themeGet("colors.subText")(props),
          borderColor: themeGet("colors.surfaceRaisedBorder")(props),
          cursor: "default",
          "&:hover, &:focus": {
            background: themeGet("colors.hoverSurface")(props),
            color: themeGet("colors.subText")(props),
            borderColor: themeGet("colors.surfaceRaisedBorder")(props),
            div: {
              color: themeGet("colors.subText")(props),
            },
            "div[class*='TagType']": {
              backgroundColor: themeGet("colors.mutedText")(props),
              color: themeGet("colors.surface")(props),
            },
          },
        },
      },
    })}
`;

const TagValueText = styled.div`
  text-decoration: ${(props) =>
    props.showStrikeThrough ? "line-through" : "none"};
  white-space: normal;
  word-break: break-word;
  max-width: 100%; /* Ensure content doesn't exceed container width */
  text-align: left;
  line-height: ${(props) =>
    props.small
      ? themeGet("fontSizes.0")(props)
      : themeGet("fontSizes.1")(props)};
  padding-bottom: 1px;
`;

const TagActionIconWrapper = styled(TagValue)`
  border-radius: ${(props) =>
    props.showRemove ? "0" : themeGet("tagScale.tagBorderRadius")};
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  padding: ${(props) =>
    props.showRemove && props.small
      ? "0 3px 0 4px"
      : props.showRemove
        ? "0 5px 0 5px"
        : props.showInfo && props.small
          ? "0 8px 0 7px"
          : props.showInfo
            ? "0 10px 0 9px"
            : props.small
              ? "0 4px 0 3px"
              : "0 6px 0 5px"};
  &:focus {
    z-index: 2;
  }
  > span {
    line-height: 0;
  }

  ${(props) =>
    variant({
      variants: {
        default: {
          borderLeft: 0,
          "&:hover, &:focus": {
            backgroundColor: themeGet("colors.primaryLightest")(props),
            color: themeGet("colors.primaryDark")(props),
            borderLeft: 0,
          },
        },
        selected: {
          borderLeft: `1px solid ${themeGet("colors.primaryDark")(props)}`,
          "&:hover": {
            borderLeftColor: themeGet("colors.primaryDark")(props),
          },
        },
        highlighted: {
          borderLeft: 0,
          "&:hover, &:focus": {
            backgroundColor: themeGet("colors.warningLightest")(props),
            color: themeGet("colors.warningDarkest")(props),
            borderLeft: 0,
          },
        },
        selectedHighlighted: {
          borderLeft: `1px solid ${themeGet("colors.warningDarker")(props)}`,
          "&:hover": {
            borderLeftColor: themeGet("colors.warningDarker")(props),
          },
        },
        disabled: {
          borderLeft: 0,
          "&:hover, &:focus": {
            backgroundColor: themeGet("colors.hoverSurface")(props),
            color: themeGet("colors.subText")(props),
            borderLeft: 0,
          },
        },
      },
    })}
`;

const TagRemoveIconWrapper = styled(TagValue)`
  border-radius: ${themeGet("tagScale.tagBorderRadius")};
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  padding: ${(props) => (props.small ? "2px 6px 2px 5px" : "2px 8px 2px 7px")};
  &:focus {
    z-index: 2;
  }

  ${(props) =>
    variant({
      variants: {
        default: {
          borderLeft: 0,
          "&:hover, &:focus": {
            backgroundColor: themeGet("colors.primaryLightest")(props),
            borderLeft: 0,
            color: themeGet("colors.primaryDark")(props),
          },
        },
        selected: {
          borderLeft: `1px solid ${themeGet("colors.primaryDark")(props)}`,
          "&:hover": {
            borderLeftColor: themeGet("colors.primaryDark")(props),
          },
        },
        highlighted: {
          borderLeft: 0,
          "&:hover, &:focus": {
            backgroundColor: themeGet("colors.warningLightest")(props),
            borderLeft: 0,
            color: themeGet("colors.warningDarkest")(props),
          },
        },
        selectedHighlighted: {
          borderLeft: `1px solid ${themeGet("colors.warningDarker")(props)}`,
          "&:hover": {
            borderLeftColor: themeGet("colors.warningDarker")(props),
          },
        },
        disabled: {
          borderLeft: 0,
          "&:hover, &:focus": {
            backgroundColor: themeGet("colors.hoverSurface")(props),
            borderLeft: 0,
            color: themeGet("colors.subText")(props),
          },
        },
      },
    })}
`;

const TagType = styled.div`
  text-transform: uppercase;
  padding: ${(props) =>
    props.small ? themeGet("space.2")(props) : themeGet("space.2")(props)};
  line-height: 1;
  white-space: normal;
  word-break: break-word;
  text-align: left;
  border-radius: ${(props) => themeGet("radii.1")(props)};
  font-size: ${(props) =>
    props.small ? "1rem" : themeGet("fontSizes.0")(props)};

  ${(props) =>
    variant({
      variants: {
        default: {
          backgroundColor: themeGet("colors.primaryLightest")(props),
          color: themeGet("colors.primaryDark")(props),
        },
        selected: {
          backgroundColor: "rgba(0,0,0,0.25)",
          color: themeGet("colors.white")(props),
        },
        highlighted: {
          backgroundColor: themeGet("colors.warning30")(props),
          color: themeGet("colors.warningDarkest")(props),
        },
        selectedHighlighted: {
          backgroundColor: themeGet("colors.warningLightest")(props),
          color: themeGet("colors.black70")(props),
        },
        disabled: {
          backgroundColor: themeGet("colors.mutedText")(props),
          color: themeGet("colors.surface")(props),
        },
      },
    })}
`;

const TagAvatarWrapper = styled.div`
  margin-left: ${(props) =>
    props.small
      ? themeGet("tagScale.tagAvatarMarginLeftSmall")
      : themeGet("tagScale.tagAvatarMarginLeftDefault")};
`;

const TagAvatar = styled(Avatar)``;

/**
 * The top two rows are an example of how tags should be used when they are selectable/unselectable. There is the option to display the cross icon or not.
 *
 * The bottom row is when you want to show tags that aren't editable.
 */

export default function Tag({
  variant = "default",
  onSelect,
  showRemove = false,
  onRemove,
  theme,
  tagType,
  showEdit = false,
  onEdit,
  showInfo = false,
  onInfo,
  showStrikeThrough = false,
  children,
  isPending,
  ariaLabel,
  small = false,
  personEntity,
  ...props
}) {
  const component = (
    <TagWrapper small={small} {...props}>
      <TagValue
        variant={variant}
        showEdit={showEdit}
        showInfo={showInfo}
        showRemove={showRemove}
        onClick={onSelect}
        small={small}
        aria-label={ariaLabel}
      >
        <Flex
          flexWrap="nowrap"
          gap={props.small ? "3px" : "4px"}
          alignItems="center"
        >
          {personEntity && (
            <TagAvatarWrapper>
              <TagAvatar
                variant={variant}
                small={small}
                imageAlt={`Avatar for ${children}`}
                image={personEntity.avatarSrc}
                initials={personEntity.initials}
                customSize={small ? "16px" : "20px"}
              />
            </TagAvatarWrapper>
          )}
          <TagValueText showStrikeThrough={showStrikeThrough} small={small}>
            {showStrikeThrough && <span className="sr-only">Striked out:</span>}
            {children}
          </TagValueText>
        </Flex>
        {tagType && (
          <TagType variant={variant} small={small} className="TagType">
            {tagType}
          </TagType>
        )}
        {isPending && <Loading />}
      </TagValue>
      {showEdit && (
        <TagActionIconWrapper
          variant={variant}
          showRemove={showRemove}
          onClick={onEdit}
          small={small}
          aria-label="Edit"
          title="Edit"
        >
          <Icon icon={["fas", "pen"]} size="sm" />
        </TagActionIconWrapper>
      )}
      {showInfo && (
        <TagActionIconWrapper
          variant={variant}
          onClick={onInfo}
          showInfo={showInfo}
          small={small}
          aria-label="View details"
          title="View details"
        >
          <Icon icon={["fas", "info"]} size="sm" />
        </TagActionIconWrapper>
      )}
      {showRemove && (
        <TagRemoveIconWrapper
          variant={variant}
          onClick={onRemove}
          small={small}
          aria-label="Remove"
          title="Remove"
        >
          <Icon icon={["fas", "times"]} size="sm" />
        </TagRemoveIconWrapper>
      )}
    </TagWrapper>
  );

  return theme ? (
    <ThemeProvider theme={theme}>{component}</ThemeProvider>
  ) : (
    component
  );
}

Tag.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  /** Specifies tag variant. Controls the visual state of the tag. */
  variant: PropTypes.oneOf([
    "default",
    "selected",
    "highlighted",
    "selectedHighlighted",
    "disabled",
  ]),
  /** On tag selected */
  onSelect: PropTypes.func,
  /** Shows the remove button */
  showRemove: PropTypes.bool,
  /** On tag remove button clicked */
  onRemove: PropTypes.func,
  /** Shows edit button */
  showEdit: PropTypes.bool,
  /** On tag edit button clicked */
  onEdit: PropTypes.func,
  /** Shows info button */
  showInfo: PropTypes.bool,
  /** On tag info button clicked */
  onInfo: PropTypes.func,
  /** Can add a type to a tag as text */
  tagType: PropTypes.string,
  /** Adds loading spinner to tag to indicate awaiting an action to complete */
  isPending: PropTypes.bool,
  /** Specifies the design theme */
  theme: PropTypes.object,
  /** Add a strikethrough to a tag value text */
  showStrikeThrough: PropTypes.bool,
  /** Applies the small variant styles */
  small: PropTypes.bool,
  /** Allows you to set a person avatar image or initials */
  personEntity: PropTypes.object,
  /** Add ariaLabel text describing to screen readers what this tag button does when pressed. e.g. Apply filter or Navigate to tag.  */
  ariaLabel: PropTypes.string,
};
